import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';

import { holdsConsoleRole, useSessionStore } from '@shared/auth/session';
import { formatPhoneForReading } from '@shared/format';
import { Alert } from '@shared/ui/Alert';

import { AuthCard, AuthHead, AuthShell } from '../_shared/AuthShell';
import {
  ActionRow,
  AuthButton,
  AuthFoot,
  Field,
  FormError,
  RequiredNote,
  useOnline,
} from '../_shared/components';
import { AUTH_ROUTES, OTP_USAGE } from '../_shared/constants';
import { authCopy } from '../_shared/copy';
import { toFieldFailure, toFormFailure, toRetryAfter, type FormFailure } from '../_shared/failure';
import { useAuthConfiguration, useCreateSession, useRequestOtp } from '../_shared/queries';
import type { AuthHandoff } from '../_shared/types';

import { copy } from './copy';
import { buildVerifySchema, type VerifyFields } from './forms.schema';
import { ResendTimer } from './ResendTimer';

/**
 * The one-time code step.
 *
 * The code completes a registration or a sign-in and the screen is the same for
 * both — no branch in the markup and no sentence that assumes either, because
 * the platform's first rule is that an existing number and a new one are
 * indistinguishable from outside.
 *
 * **One input, no segmented boxes and no fixed length in the markup.** The
 * configuration declares a validity window; the code's own length is a served
 * value, so it narrows the schema and never becomes a box count. A row of six
 * squares would be a configuration value drawn into the DOM.
 *
 * The number is echoed above the field for one reason: so a mistyped digit is
 * caught before the operator waits for a message that will never arrive. It is
 * a human surface, so it carries Arabic-Indic digits inside `<bdi>` — the
 * bidirectional algorithm reorders a Latin-digit run beside Arabic prose, and
 * a number reordered is a different number.
 */
export function OtpVerifyPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const online = useOnline();

  const handoff = location.state as AuthHandoff | null;

  const [failure, setFailure] = useState<FormFailure | null>(null);
  const [roleRefused, setRoleRefused] = useState(false);
  const [bar, setBar] = useState({ until: handoff?.barredUntil ?? 0, epoch: 0 });

  const configuration = useAuthConfiguration();
  const requestOtp = useRequestOtp();
  const createSession = useCreateSession();

  const schema = useMemo(
    () => buildVerifySchema(configuration.data?.otp_code_length),
    [configuration.data?.otp_code_length],
  );

  const form = useForm<VerifyFields>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: { code: '' },
  });

  useEffect(() => {
    document.title = copy.documentTitle;
  }, []);

  /**
   * Reached from the sign-in screen's primary action, and from nowhere else.
   * A direct hit carries no number, and there is no field here to supply one —
   * the identity screen is where a number is entered. Returning there is the
   * honest answer; drawing a form that cannot be submitted is not.
   */
  if (!handoff?.phone) {
    return <Navigate to={AUTH_ROUTES.login} replace />;
  }

  const phone = handoff.phone;
  const next = handoff.next ?? '/';
  const pending = createSession.isPending;

  async function onSubmit(values: { code: string }) {
    setFailure(null);

    try {
      await createSession.mutateAsync({ phone, code: values.code });

      // Authenticated, and the console still may not open. The session is
      // cleared rather than left live behind a refusal, and the operator is
      // returned to the identity screen carrying the reason — not to a 404,
      // which would misdescribe a page that exists.
      if (!holdsConsoleRole(useSessionStore.getState().role)) {
        useSessionStore.getState().clear();
        setRoleRefused(true);
        return;
      }

      navigate(next, { replace: true });
    } catch (error) {
      const fieldFailure = toFieldFailure(error);
      if (fieldFailure?.field === 'code') {
        form.setError('code', { message: fieldFailure.message });
      }
      setFailure(toFormFailure(error));
    }
  }

  /**
   * Move the resend bar to a new deadline, and record it where a reload will find it.
   *
   * The deadline is written back into the router's location state as well as into
   * component state, because `history.state` is what survives a refresh: a resend that
   * only updated memory would leave a reloaded page re-reading the deadline it was
   * handed on arrival — the same defect as storing a duration, one press later.
   *
   * `replace` rather than a push: this is the same screen with a newer fact on it, not
   * a place the back button should return to.
   */
  function barUntil(until: number) {
    setBar((current) => ({ until, epoch: current.epoch + 1 }));
    navigate(location.pathname, {
      replace: true,
      state: { ...(handoff as AuthHandoff), barredUntil: until },
    });
  }

  async function onResend() {
    setFailure(null);

    try {
      const { retry_after } = await requestOtp.mutateAsync({
        phone,
        usage_type: OTP_USAGE.login,
      });
      barUntil(Date.now() + retry_after * 1000);
    } catch (error) {
      // A `429` here is the resend bar itself answering: the previous code is
      // still alive. The countdown is re-seeded from the body integer rather
      // than from the header, per the platform's rate-limit contract.
      const retryAfter = toRetryAfter(error);
      if (retryAfter !== null) {
        barUntil(Date.now() + retryAfter * 1000);
      }
      setFailure(toFormFailure(error));
    }
  }

  return (
    <AuthShell>
      <AuthHead title={copy.head.title}>
        {copy.head.ledePrefix}
        <bdi>{formatPhoneForReading(phone)}</bdi>
        {copy.head.ledeSuffix}
      </AuthHead>

      {roleRefused ? (
        <Alert kind="warning" title={authCopy.roleNotice.title} className="mb-5">
          {authCopy.roleNotice.body}
        </Alert>
      ) : null}

      {failure ? <FormError message={failure.message} tone={failure.tone} /> : null}

      <AuthCard>
        <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
          <RequiredNote />

          <Field
            {...form.register('code')}
            id="a-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            dir="ltr"
            codeInput
            label={copy.code.label}
            requiredMark
            hint={copy.code.hint}
            {...(form.formState.errors.code?.message ? { error: form.formState.errors.code.message } : {})}
          />

          <ActionRow>
            <AuthButton type="submit" size="lg" disabled={pending}>
              {online ? copy.actions.confirm : copy.actions.offline}
            </AuthButton>
          </ActionRow>
        </form>

        <ResendTimer
          until={bar.until}
          epoch={bar.epoch}
          pending={requestOtp.isPending}
          online={online}
          onResend={() => void onResend()}
        />
      </AuthCard>

      {/*
        Back to the identity screen when the number was wrong. Not a new
        capability — it is a re-entry into the same row this screen came from.
      */}
      <AuthFoot>
        <Link to={AUTH_ROUTES.login}>{copy.foot.wrongNumber}</Link>
      </AuthFoot>
    </AuthShell>
  );
}
